import type { Accessor } from 'solid-js';

import type { ReferenceType } from '@data-access/geospatial/fx-workspace-service';

import { createFxWorkspaceDraftStore } from './create-fx-workspace-draft-store';
import type { SyncFxWorkspaceReferencesInput } from './fx-workspace-repository';
import type { FxWorkspaceDraftReferencesApi } from './use-fx-workspace-draft-references.types';

export function useFxWorkspaceDraftReferences(options: {
  isEditing: Accessor<boolean>;
}): FxWorkspaceDraftReferencesApi {
  const draft = createFxWorkspaceDraftStore();

  const toggleDraftReference = (refTrn: string, type: ReferenceType): void => {
    if (!options.isEditing()) {
      return;
    }

    draft.setSelection([refTrn], !draft.draftReferences().has(refTrn), type);
  };

  const setDraftReferenceSelection = (
    refTrns: readonly string[],
    selected: boolean,
    type: ReferenceType
  ): void => {
    if (!options.isEditing()) {
      return;
    }

    draft.setSelection(refTrns, selected, type);
  };

  const diff = (
    current: ReadonlySet<string>
  ): SyncFxWorkspaceReferencesInput => {
    const references = draft.draftReferences();
    const types = draft.draftReferenceTypes();
    // replace and mergeLoaded copy saved refs without a type. Only a ref
    // selected in this edit has a type, so a missing type is not an add.
    const toAdd = [...references].flatMap((refTrn) => {
      if (current.has(refTrn)) {
        return [];
      }

      const type = types.get(refTrn);
      if (type === undefined) {
        return [];
      }

      return [{ refTrn, type }];
    });
    const toRemove = [...current].filter((refTrn) => !references.has(refTrn));

    return { toAdd, toRemove };
  };

  return {
    draftReferences: draft.draftReferences,
    reset: draft.reset,
    replace: draft.replace,
    mergeLoaded: draft.mergeLoaded,
    toggleDraftReference,
    isDraftReference: (refTrn: string): boolean =>
      draft.draftReferences().has(refTrn),
    setDraftReferenceSelection,
    diff,
  };
}
